import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCurrentUser } from '../../contexts/UserContext';
import { usePyodide } from '../../hooks/usePyodide';
import { PyodideContext } from './PyodideContext';
import MarkdownCell from './MarkdownCell';
import CodeCell from './CodeCell';
import ChallengeCell from './ChallengeCell';
import VariableInspector from './VariableInspector';
import Console from './Console';
import Scratchpad from './Scratchpad';
import AIAssistant from './AIAssistant';
import '../../styles/pythonLab.css';

import { LESSONS, UNITS } from '../../data/pythonLessons/registry';

function PyodideStatus({ loading, error }) {
  if (loading) return (
    <span className="pylab-runtime-status pylab-runtime-status--loading">
      <span className="pylab-spinner" aria-hidden="true" /> Loading Python…
    </span>
  );
  if (error) return (
    <span className="pylab-runtime-status pylab-runtime-status--error" title={error}>
      Python unavailable
    </span>
  );
  return (
    <span className="pylab-runtime-status pylab-runtime-status--ready">Python ready</span>
  );
}

function HamburgerIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="3" y1="6"  x2="21" y2="6"  />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function LessonNav({ currentId, onClose }) {
  const navigate = useNavigate();

  return (
    <>
      <div className="pylab-nav-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="pylab-nav-drawer" role="dialog" aria-label="Lesson navigation">
        <div className="pylab-nav-drawer-head">
          <span className="pylab-nav-drawer-title">50-Class Curriculum</span>
          <button type="button" className="pylab-nav-drawer-close" onClick={onClose} aria-label="Close navigation">×</button>
        </div>
        <div className="pylab-nav-list">
          {UNITS.map((unit) => (
            <div key={unit.title} className="pylab-nav-unit">
              <p className="pylab-nav-unit-label">{unit.title}</p>
              {unit.lessons.map((lesson) => (
                <button
                  key={lesson.id}
                  type="button"
                  className={`pylab-nav-item${lesson.id === currentId ? ' pylab-nav-item--active' : ''}`}
                  onClick={() => { navigate(`/python/${lesson.id}`); onClose(); }}
                >
                  <span className="pylab-nav-item-title">{lesson.title}</span>
                  {lesson.estimatedMinutes && (
                    <span className="pylab-nav-item-meta">{lesson.estimatedMinutes} min</span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default function PythonLab() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { pyodide, loading, error } = usePyodide();

  const [view, setView] = useState('notebook');
  const [namespace, setNamespace] = useState({});
  const [lastOutput, setLastOutput] = useState(null);
  const [aiContext, setAiContext] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  const aiPanelRef = useRef(null);
  const lesson = useMemo(() => LESSONS[lessonId] || null, [lessonId]);

  const handleRun = useCallback(({ heading, code, error: runError, stdout, stderr, namespace: ns }) => {
    setNamespace(ns);
    setLastOutput({ heading, stdout, stderr, error: runError });
    setAiContext({ heading, code, error: runError });
  }, []);

  if (!currentUser) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  if (!lesson) {
    return (
      <div className="pylab-shell">
        <header className="pylab-header">
          <div className="pylab-header-left">
            <button type="button" className="pylab-hamburger-btn" onClick={() => setNavOpen(true)} aria-label="Open lesson navigation">
              <HamburgerIcon />
            </button>
            <span className="pylab-header-brand">Python Lab</span>
          </div>
          <div className="pylab-header-right">
            <button type="button" className="pylab-close-btn" onClick={() => navigate(-1)}>Close</button>
          </div>
        </header>
        <div className="pylab-not-found">
          <p>Lesson <strong>{lessonId}</strong> not found.</p>
          <button type="button" className="pylab-close-btn" onClick={() => navigate(-1)}>Go back</button>
        </div>
        {navOpen && <LessonNav currentId={lessonId} onClose={() => setNavOpen(false)} />}
      </div>
    );
  }

  return (
    <PyodideContext.Provider value={{ pyodide }}>
      <div className="pylab-shell">

        {/* ── Top header ─────────────────────────────────────────────────── */}
        <header className="pylab-header">
          <div className="pylab-header-left">
            <button
              type="button"
              className="pylab-hamburger-btn"
              onClick={() => setNavOpen(true)}
              aria-label="Open lesson navigation"
            >
              <HamburgerIcon />
            </button>
            <span className="pylab-header-brand">Python Lab</span>
          </div>
          <div className="pylab-header-right">
            <PyodideStatus loading={loading} error={error} />
            <button type="button" className="pylab-close-btn" onClick={() => navigate(-1)}>
              Close
            </button>
          </div>
        </header>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <div className="pylab-body">

          {/* Left panel */}
          <main className="pylab-main">

            {/* Panel sub-header: lesson title + view toggle */}
            <div className="pylab-panel-header">
              <span className="pylab-panel-title">{lesson.title}</span>
              {lesson.estimatedMinutes && (
                <span className="pylab-duration">{lesson.estimatedMinutes} min</span>
              )}
              <div className="pylab-view-toggle" role="group" aria-label="View mode">
                <button
                  type="button"
                  className={`pylab-view-btn${view === 'notebook' ? ' pylab-view-btn--active' : ''}`}
                  onClick={() => setView('notebook')}
                >
                  Notebook
                </button>
                <button
                  type="button"
                  className={`pylab-view-btn${view === 'scratchpad' ? ' pylab-view-btn--active' : ''}`}
                  onClick={() => setView('scratchpad')}
                >
                  Scratchpad
                </button>
              </div>
            </div>

            {/* Panel content */}
            {view === 'notebook' ? (
              <div className="pylab-notebook">
                {lesson.cells.map((cell) => {
                  if (cell.type === 'markdown')  return <MarkdownCell  key={cell.id} cell={cell} />;
                  if (cell.type === 'code')       return <CodeCell      key={cell.id} cell={cell} onRun={handleRun} />;
                  if (cell.type === 'challenge')  return <ChallengeCell key={cell.id} cell={cell} onRun={handleRun} />;
                  return null;
                })}
                <div className="pylab-notebook-end" aria-hidden="true" />
              </div>
            ) : (
              <div className="pylab-scratchpad-view">
                <Scratchpad />
              </div>
            )}
          </main>

          {/* Right rail — Variables (top 50%) + Console (bottom 50%) */}
          <aside className="pylab-rail">
            <div className="pylab-rail-section">
              <div className="pylab-rail-section-head">
                Variables
                {Object.keys(namespace).length > 0 && (
                  <span className="pylab-var-count">{Object.keys(namespace).length}</span>
                )}
              </div>
              <div className="pylab-rail-section-body">
                <VariableInspector namespace={namespace} />
              </div>
            </div>

            <div className="pylab-rail-section">
              <div className="pylab-rail-section-head">Console</div>
              <div className="pylab-rail-section-body">
                <Console lastOutput={lastOutput} />
              </div>
            </div>
          </aside>

        </div>

        {/* ── Lesson nav drawer ───────────────────────────────────────────── */}
        {navOpen && <LessonNav currentId={lessonId} onClose={() => setNavOpen(false)} />}

        {/* ── Floating AI chat ────────────────────────────────────────────── */}
        {aiOpen && (
          <div className="pylab-ai-float" ref={aiPanelRef}>
            <div className="pylab-ai-float-header">
              <span className="pylab-ai-float-title">AI Assistant</span>
              <button
                type="button"
                className="pylab-ai-float-close"
                onClick={() => setAiOpen(false)}
                aria-label="Close AI assistant"
              >
                ×
              </button>
            </div>
            <div className="pylab-ai-float-body">
              <AIAssistant lessonTitle={lesson.title} aiContext={aiContext} />
            </div>
          </div>
        )}

        <button
          type="button"
          className={`pylab-ai-fab${aiOpen ? ' pylab-ai-fab--open' : ''}`}
          onClick={() => setAiOpen((v) => !v)}
          aria-label={aiOpen ? 'Close AI assistant' : 'Open AI assistant'}
          title="AI Assistant"
        >
          <ChatIcon />
        </button>

      </div>
    </PyodideContext.Provider>
  );
}
